import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { crearFacturaCanDeactivate } from './features/facturacion/crear-factura/crear-factura-deactivate.guard';
import { convertirCanDeactivate } from './features/cuentas/convertir/convertir-deactivate.guard';
import { articuloFormCanDeactivate } from './features/configuracion/articulos/articulo-form/articulo-form-deactivate.guard';
import { contratoCrearCanDeactivate } from './features/contratos/contrato-crear/contrato-crear-deactivate.guard';
import { contratoGestionarCanDeactivate } from './features/contratos/contrato-gestionar/contrato-gestionar-deactivate.guard';
import { contratoDetalleCanDeactivate } from './features/contratos/contrato-detalle/contrato-detalle-deactivate.guard';
import { expedienteDetalleCanDeactivate } from './features/expedientes/expediente-detalle/expediente-detalle-deactivate.guard';
import { MainLayoutComponent } from './layout/main-layout.component';

/** Listados del menú principal: se cachean al volver atrás (RouteReuseStrategy). */
const REUSE_LIST = { reuseList: true } as const;

/** Pestañas bajo `cuentas/:idCuenta` y `partners/:idCuenta` (mismo layout). */
const CUENTA_FICHA_CHILDREN: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'panel' },
  {
    path: 'prospecto',
    loadComponent: () =>
      import('./features/cuentas/prospecto/prospecto-content.component').then(
        (m) => m.ProspectoContentComponent,
      ),
  },
  {
    path: 'panel',
    loadComponent: () => import('./features/cuentas/panel/panel.component').then((m) => m.PanelComponent),
  },
  {
    path: 'presupuestos',
    loadComponent: () =>
      import('./features/cuentas/presupuestos/presupuestos.component').then((m) => m.PresupuestosComponent),
  },
  {
    path: 'informacion',
    loadComponent: () =>
      import('./features/cuentas/informacion/informacion.component').then((m) => m.InformacionComponent),
  },
  {
    path: 'contratacion',
    loadComponent: () =>
      import('./features/cuentas/contratacion/contratacion.component').then((m) => m.ContratacionComponent),
  },
  {
    path: 'contratos',
    loadComponent: () =>
      import('./features/cuentas/partner-contratos/partner-contratos.component').then(
        (m) => m.PartnerContratosComponent,
      ),
  },
  {
    path: 'expedientes',
    loadComponent: () =>
      import('./features/cuentas/partner-expedientes/partner-expedientes.component').then(
        (m) => m.PartnerExpedientesComponent,
      ),
  },
  {
    path: 'facturacion',
    loadComponent: () =>
      import('./features/cuentas/facturacion/facturacion.component').then((m) => m.FacturacionComponent),
  },
  {
    path: 'cuentas',
    loadComponent: () =>
      import('./features/cuentas/partner-cuentas/partner-cuentas.component').then(
        (m) => m.PartnerCuentasComponent,
      ),
  },
];

/** Ficha, convertir y acceso para cuentas normales y partners (prefijo distinto). */
function cuentaDetailRoutes(segment: 'cuentas' | 'partners'): Routes {
  return [
    {
      path: `${segment}/:idCuenta/convertir`,
      loadComponent: () => import('./features/cuentas/convertir/convertir.component').then((m) => m.ConvertirComponent),
      canDeactivate: [convertirCanDeactivate],
    },
    {
      path: `${segment}/:idCuenta/acceso`,
      loadComponent: () => import('./features/cuentas/acceso/acceso.component').then((m) => m.AccesoComponent),
    },
    {
      path: `${segment}/:idCuenta`,
      loadComponent: () =>
        import('./features/cuentas/cuenta-layout/cuenta-layout.component').then((m) => m.CuentaLayoutComponent),
      children: CUENTA_FICHA_CHILDREN,
    },
  ];
}

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'cuentas' },
      { path: 'cuentas', data: { distribuidor: 0, ...REUSE_LIST }, loadComponent: () => import('./features/cuentas/cuentas.component').then(m => m.CuentasComponent) },
      {
        path: '401',
        redirectTo: '403',
        pathMatch: 'full',
      },
      {
        path: '403',
        loadComponent: () =>
          import('./features/errors/unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
      },
      { path: 'facturas', data: REUSE_LIST, loadComponent: () => import('./features/facturacion/facturacion-listado/facturacion-listado.component').then(m => m.FacturacionListadoComponent) },
      {
        path: 'facturador/facturar-manual',
        loadComponent: () =>
          import('./features/facturador/facturador-facturar-manual.component').then(
            (m) => m.FacturadorFacturarManualComponent,
          ),
      },
      {
        path: 'facturador',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/facturador/facturador-listado.component').then((m) => m.FacturadorListadoComponent),
      },
      {
        path: 'remesas/nueva',
        loadComponent: () =>
          import('./features/remesas/remesa-crear/remesa-crear.component').then((m) => m.RemesaCrearComponent),
      },
      {
        path: 'remesas/bloquear',
        loadComponent: () =>
          import('./features/remesas/remesa-bloquear-facturas/remesa-bloquear-facturas.component').then(
            (m) => m.RemesaBloquearFacturasComponent,
          ),
      },
      {
        path: 'remesas/:id_remesa',
        loadComponent: () =>
          import('./features/remesas/remesa-detalle/remesa-detalle.component').then((m) => m.RemesaDetalleComponent),
      },
      {
        path: 'remesas',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/remesas/remesas-listado/remesas-listado.component').then(
            (m) => m.RemesasListadoComponent,
          ),
      },
      {
        path: 'expedientes/crear',
        loadComponent: () =>
          import('./features/expedientes/expediente-crear/expediente-crear.component').then(
            (m) => m.ExpedienteCrearComponent,
          ),
      },
      {
        path: 'expedientes/:id_expediente/editar',
        loadComponent: () =>
          import('./features/expedientes/expediente-crear/expediente-crear.component').then(
            (m) => m.ExpedienteCrearComponent,
          ),
      },
      {
        path: 'expedientes/:id_expediente_linea',
        canDeactivate: [expedienteDetalleCanDeactivate],
        loadComponent: () =>
          import('./features/expedientes/expediente-detalle/expediente-detalle.component').then(
            (m) => m.ExpedienteDetalleComponent,
          ),
      },
      {
        path: 'expedientes',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/expedientes/expedientes-listado/expedientes-listado.component').then(
            (m) => m.ExpedientesListadoComponent,
          ),
      },
      {
        path: 'actividad',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/actividad/actividad-listado/actividad-listado.component').then(
            (m) => m.ActividadListadoComponent,
          ),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./features/support/support.component').then((m) => m.SupportComponent),
      },
      {
        path: 'cuotas',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/cuotas/cuotas-listado/cuotas-listado.component').then(
            (m) => m.CuotasListadoComponent,
          ),
      },
      {
        path: 'contratos',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/contratos/contratos-listado/contratos-listado.component').then(
            (m) => m.ContratosListadoComponent,
          ),
      },
      {
        path: 'contratos/crear',
        loadComponent: () =>
          import('./features/contratos/contrato-crear/contrato-crear.component').then(
            (m) => m.ContratoCrearComponent,
          ),
        canDeactivate: [contratoCrearCanDeactivate],
      },
      {
        path: 'contratos/:id_contrato/editar',
        loadComponent: () =>
          import('./features/contratos/contrato-crear/contrato-crear.component').then(
            (m) => m.ContratoCrearComponent,
          ),
        canDeactivate: [contratoCrearCanDeactivate],
      },
      {
        path: 'contratos/:id_contrato/gestionar',
        loadComponent: () =>
          import('./features/contratos/contrato-gestionar/contrato-gestionar.component').then(
            (m) => m.ContratoGestionarComponent,
          ),
        canDeactivate: [contratoGestionarCanDeactivate],
      },
      {
        path: 'contratos/:id_contrato',
        loadComponent: () =>
          import('./features/contratos/contrato-detalle/contrato-detalle.component').then(
            (m) => m.ContratoDetalleComponent,
          ),
        canDeactivate: [contratoDetalleCanDeactivate],
      },
      {
        path: 'facturas/nueva',
        loadComponent: () =>
          import('./features/facturacion/crear-factura/crear-factura.component').then(
            (m) => m.CrearFacturaComponent,
          ),
        canDeactivate: [crearFacturaCanDeactivate],
      },
      {
        path: 'facturas/:id_factura/editar',
        loadComponent: () =>
          import('./features/facturacion/crear-factura/crear-factura.component').then(
            (m) => m.CrearFacturaComponent,
          ),
        canDeactivate: [crearFacturaCanDeactivate],
      },
      { path: 'facturas/:id_factura', loadComponent: () => import('./features/facturacion/factura-detalle/factura-detalle.component').then(m => m.FacturaDetalleComponent) },
      ...cuentaDetailRoutes('cuentas'),
      ...cuentaDetailRoutes('partners'),
      { path: 'partner/:idCuenta', redirectTo: 'partners/:idCuenta' },
      { path: 'partner/:idCuenta/:tab', redirectTo: 'partners/:idCuenta/:tab' },
      {
        path: 'partners',
        data: { distribuidor: 1, ...REUSE_LIST },
        loadComponent: () => import('./features/cuentas/cuentas.component').then((m) => m.CuentasComponent),
      },
      {
        path: 'configuracion/industria',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/industria/industrias-listado.component').then(
            (m) => m.IndustriasListadoComponent,
          ),
      },
      {
        path: 'configuracion/mercado',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/mercado/mercados-listado.component').then(
            (m) => m.MercadosListadoComponent,
          ),
      },
      {
        path: 'configuracion/regiones',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/region/regiones-listado.component').then(
            (m) => m.RegionesListadoComponent,
          ),
      },
      {
        path: 'configuracion/paises',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/pais/paises-listado.component').then(
            (m) => m.PaisesListadoComponent,
          ),
      },
      {
        path: 'configuracion/tipos_ingreso',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/tipo-ingreso/tipos-ingreso-listado.component').then(
            (m) => m.TiposIngresoListadoComponent,
          ),
      },
      {
        path: 'configuracion/lineas_de_producto',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/lineas-de-producto/lineas-de-producto-listado.component').then(
            (m) => m.LineasDeProductoListadoComponent,
          ),
      },
      {
        path: 'configuracion/categorias_contables',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/categorias-contables/categorias-contables-listado.component').then(
            (m) => m.CategoriasContablesListadoComponent,
          ),
      },
      {
        path: 'configuracion/grupos_empresariales',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/grupos-empresariales/grupos-empresariales-listado.component').then(
            (m) => m.GruposEmpresarialesListadoComponent,
          ),
      },
      {
        path: 'configuracion/articulos/nuevo',
        redirectTo: 'configuracion/articulos/crear',
        pathMatch: 'full',
      },
      {
        path: 'configuracion/articulos/crear',
        loadComponent: () =>
          import('./features/configuracion/articulos/articulo-form/articulo-form.component').then(
            (m) => m.ArticuloFormComponent,
          ),
        canDeactivate: [articuloFormCanDeactivate],
      },
      {
        path: 'configuracion/articulos/:id_articulo/editar',
        loadComponent: () =>
          import('./features/configuracion/articulos/articulo-form/articulo-form.component').then(
            (m) => m.ArticuloFormComponent,
          ),
        canDeactivate: [articuloFormCanDeactivate],
      },
      {
        path: 'configuracion/articulos',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/articulos/articulos-listado.component').then(
            (m) => m.ArticulosListadoComponent,
          ),
      },
      {
        path: 'configuracion/familias/nuevo',
        redirectTo: 'configuracion/familias',
        pathMatch: 'full',
      },
      {
        path: 'configuracion/familias/:id_familia/editar',
        redirectTo: 'configuracion/familias',
        pathMatch: 'full',
      },
      {
        path: 'configuracion/familias',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/familias/familias-listado.component').then(
            (m) => m.FamiliasListadoComponent,
          ),
      },
      {
        path: 'configuracion/usuarios',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/usuarios/usuarios-listado.component').then(
            (m) => m.UsuariosListadoComponent,
          ),
      },
      {
        path: 'configuracion/roles',
        data: REUSE_LIST,
        loadComponent: () =>
          import('./features/configuracion/roles/roles-listado.component').then(
            (m) => m.RolesListadoComponent,
          ),
      },
      { path: 'configuracion', loadComponent: () => import('./features/cuentas/cuentas.component').then(m => m.CuentasComponent) },
    ],
  },
];
